// SignInPage.tsx
// NOTE - The Bot Handling logic for this SignInPage is located in ../components/header/Header.jsx
import React, { useState, useEffect, useRef } from "react";
import { create } from 'zustand'; 
import { handleSignIn } from "../utils/auth";
import useDeviceType from "../utils/getAgent";
import { OrgSettings } from '../tools/orgSettings';
import { usePreAuthOrg } from '../utils/usePreAuthOrg';
import { PreAuthNotice } from '../components/header/PreAuthNotice';
import { setFavicon } from '../utils/favicon';
import { iconSources } from '../utils/orgIcon';
import SignInPageGuest from './SignInPageGuest';

const usePasswordStore = create((set: any) => ({// Zustand store
  showPassword: false,
  password: "",
  toggleShowPassword: () => set((state: any) => ({ showPassword: !state.showPassword })),
  setShowPassword: (showPassword: boolean) => set(() => ({ showPassword })),
  setPassword: (password: string) => set(() => ({ password })),
}));

const getStoredBotId = () => {
  const fromLastBot = localStorage.getItem('lastBotId');
  if (fromLastBot) return fromLastBot;

  const fromSelectedBot = localStorage.getItem('selectedBot');
  if (fromSelectedBot) return fromSelectedBot;

  try {
    const rawSelected = localStorage.getItem('SelectedBot');
    if (rawSelected) {
      const parsed = JSON.parse(rawSelected);
      if (parsed?.botid) return parsed.botid;
    }
  } catch {
    // ignore parse issues
  }

  try {
    const guestOrigin = sessionStorage.getItem('guestOrigin') || localStorage.getItem('guestOrigin');
    if (guestOrigin) {
      const originUrl = new URL(guestOrigin);
      const originId = originUrl.searchParams.get('id');
      if (originId) return originId;
    }
  } catch {
    // ignore URL parse issues
  }

  return null;
};

const SignInPageAuthed = ({ botId: resolvedBotId, org: resolvedOrg }: { botId: string; org: OrgSettings | null }) => {
  const { showPassword, toggleShowPassword, setShowPassword, password, setPassword } = usePasswordStore();;//zustand
  const [email, setEmail] = useState("");
  //const [password, setPassword] = useState("");// REact state replaced by zustand
  const [errorMessage, setErrorMessage] = useState("");
  const [user, setUserState] = useState<any>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const logoPicWhite = "/assets/app/logo192.png";
  const deviceType = useDeviceType();

  useEffect(() => {
    setShowPassword(false);
  }, [setShowPassword]);
  
  const queryParams = new URLSearchParams(window.location.search);
  // [S2-B B2] botId + org arrive resolved from the DB (GET /agent-bootstrap) via the SignInPage
  // wrapper below — was: a Nova default, a dead resolveDNS branch behind it, the compiled
  // settings.json `orgs` map (with a mistyped fallback key) and system.json's guestmode list.
  let botId = resolvedBotId;
  let prompt = queryParams.get("prompt") || '';

  //Clear localstore — ONCE per mount (2026-10-28). This ran on EVERY render, so the re-render
  // queued by handleSignIn's setUserState wiped the 'user' it had just written, in the window
  // between location.assign('/ibot') and this page unloading.
  const clearedStoreRef = useRef(false);
  if (!clearedStoreRef.current) {
    clearedStoreRef.current = true;
    localStorage.removeItem("user");
    localStorage.removeItem("org");
    localStorage.removeItem("lastBotId");
    localStorage.removeItem("botId");
  }

  // Assign Org from the resolved record's branding
  const org: OrgSettings | null = resolvedOrg;
 // Used by header
  localStorage.setItem("org", JSON.stringify(org));

  const handleSubmit = async () => {
    if (!email || !password) {
      setErrorMessage("Email and Password are required.");
      return;
    }

    setLoading(true);
    const loadingElement = document.querySelector(".loading");
    if (loadingElement) {
      loadingElement.textContent = "Signing In...";
    }
    if(!botId){
      botId = "asst_M3yCPGj7R4YKciQ4ZyNNyzxT";// HTP Assistant
      localStorage.setItem("lastBotId", botId); 
    
    }
    // Pass the prompt parameter to handleSignIn
    await handleSignIn(email, password, setUserState, setErrorMessage, botId, prompt || undefined);
    
    setLoading(false);
  };

  // Fix - 040525 - Clear all Cookies on Signup
     useEffect(() => {
      // ✅ Step 1: Full cookie and localStorage clear on SignIn page load

      // Clear all cookies under current domain
      document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });

      // ✅ Step 2: Attempt aggressive multi-domain cookie clearing
      const hostnameParts = window.location.hostname.split(".");
      for (let i = 0; i < hostnameParts.length - 1; i++) {
        const domain = "." + hostnameParts.slice(i).join(".");
        document.cookie.split(";").forEach((cookie) => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
          }
        });
      }

      // Clear localStorage user
      localStorage.removeItem("user");

    // ✅ Step 3: Clear sessionStorage completely
       sessionStorage.clear();

    // ✅ Step 4: Reset any globally cached or in-memory user state
    if (typeof window !== 'undefined') {
      (window as any).user = null;
    }
    // ✅ Step 5: Reload the page to enforce cookie clearance in all browsers
      if (window.location.search.includes("session") || window.location.search.includes("token")) {
        window.location.replace(window.location.pathname);
      }

    }, []);


    useEffect(() => {// Set org in localStorage
      if (!org) return;
    
      // Set cookie
      const encodedOrg = encodeURIComponent(JSON.stringify(org));
      document.cookie = `org=${encodedOrg}; path=/; SameSite=Lax`;
    
      // Set dynamic title
      document.title = org.title || org.name || 'AI Assistant';
    
      // Favicon: the agent's square icon, else its logo, fitted and never stretched (same as the Header)
      setFavicon(iconSources(org, ['/favicon.png']));
    
      // Open Graph image tag
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', org?.logo || '/org/robot-factory-sq-logo.png');
    
      // Apple app title
      let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!appleTitle) {
        appleTitle = document.createElement('meta');
        appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
        document.head.appendChild(appleTitle);
      }
      appleTitle.setAttribute('content', org.title || org.name || 'Assistant');
    
      const containerStyle = org.containerStyle || {};
      const headerBackColor = org.headerBackColor || 'transparent';
      const containerDiv = document.getElementsByClassName('container');

      console.log(`Found ${containerDiv.length} container div(s)`);
      if (containerDiv.length > 0) {
        console.log(`Applying container styles: backgroundColor: ${containerStyle.backgroundColor}, headerBackColor: ${headerBackColor}`);
        Array.from(containerDiv).forEach((div, index) => {
          const el = div as HTMLElement;
          console.log(`Styling containerDiv[${index}]`);
          el.style.backgroundColor = containerStyle.backgroundColor || 'transparent';
          el.style.color = org?.bodyStyle?.color || 'inherit';
          el.style.fontFamily = org?.fontFace || 'Arial, sans-serif';
        });
      } else {
        console.log('No container divs found');
      }

      const color = org.bodyStyle?.color || 'inherit';
      const bg = org.footer?.backcolor || 'transparent';
    
      const footerDivs = document.getElementsByClassName('signInFooter');
      console.log(`Found ${footerDivs.length} footer div(s)`);
      
      if (footerDivs.length > 0) {

        console.log(`Applying styles with color: ${color}, backgroundColor: ${bg}`);
      
        Array.from(footerDivs).forEach((footerDiv, index) => {
          const el = footerDiv as HTMLElement;
          console.log(`Styling footerDiv[${index}]`);
      
          el.style.backgroundColor = bg;
          el.style.color = color;
      
          const styledDescendants = el.querySelectorAll('[style]');
          console.log(`Found ${styledDescendants.length} descendants with inline styles`);
      
          styledDescendants.forEach((descendant, i) => {
            (descendant as HTMLElement).style.color = '';
            console.log(`Cleared inline color on descendant[${i}]`);
          });
      
          const allDescendants = el.querySelectorAll('*');
          console.log(`Applying color to ${allDescendants.length} descendants`);
      
          allDescendants.forEach((descendant, i) => {
            (descendant as HTMLElement).style.setProperty('color', color, 'important');
            if (descendant.tagName.toLowerCase() === 'a') {
              (descendant as HTMLElement).style.setProperty('text-decoration', 'none', 'important');
              console.log(`Set link styles on descendant[${i}]`);
            }
          });
        });
      } else {
        console.log('No footer divs found');
      }
      
      
    }, [org]);
  
  
  useEffect(() => {
    //console.log('✅ Password Submit: Enter key listener on password field to trigger signInButton click');

    const timer = setTimeout(() => {
      const passwordInput = document.getElementById('passwordInput') as HTMLInputElement;
      if (passwordInput) {
        const handlePasswordKeydown = (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            const signInButton = document.getElementById('signInButton');
            if (signInButton) {
              signInButton.click();
            }
          }
        };

        passwordInput.addEventListener('keydown', handlePasswordKeydown);

        // Cleanup
        return () => {
          passwordInput.removeEventListener('keydown', handlePasswordKeydown);
        };
      }
    }, 0); // delay to wait for DOM

    return () => clearTimeout(timer);

  }, []);


  return (
    <div className="signInWrapper" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {org && (
        <style>{`
          body, html, #root {
            background-color: ${org.containerStyle.backgroundColor} !important;
            font-family: ${org.fontFace};
          }
          #signInContainer, .signInFooter {
            background-color: ${org.containerStyle.backgroundColor} !important;
          }
          .title, .subtitle, .edition, .infoText, .signInFooter, .errorStatus,
          input, input::placeholder, button, a, h2, h3, p, span {
            color: ${org.bodyStyle?.color || '#555555'} !important;
          }
          .signInButton, .signUpButton {
            background-color: ${org.buttonStyle.backgroundColor} !important;
            color: ${org.buttonStyle.color} !important;
          }
        `}</style>
      )}

      <div
        className={`signInPageContainer ${deviceType}`}
        style={org ? { color: org?.bodyStyle?.color, backgroundColor: org?.containerStyle?.backgroundColor } : {}}
        >

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div
            id="signInContainer"
            className="signInContainer"
            style={{
              ...org?.containerStyle,
              flexGrow: 1,
              overflowY: "auto",
              paddingBottom: "6rem", // ✅ gives breathing room above footer on short screens
            }}
          >
            {errorMessage && <div className="errorStatus">{errorMessage}</div>}

            <div className="signInUp-logo-container" id="signInUp-logo-container">
              <a href={org?.homepage || "https://www.cloudmediaworks.com"} target="_blank">
                <img
                  id="signInUp-header-logo"
                  className="signInUp-header-logo header-toolbar-image"
                  src={org?.logo || logoPicWhite}
                  alt={org?.name ? `${org.name} Thumbnail` : "logo"}
                />
              </a>
            </div>

            <div className="signInTitleBlock">
              <h2 className="title">{org?.title || "ROBOT FACTORY"}</h2>
              <hr id="hr-signin" />
              <h3 className="subtitle">{org?.subtitle || "High Performance Leaders"}</h3>
              <p className="edition">{org?.edition || "Skills Edition"}</p>
            </div>

          {/* SignIn Block */}
          <form id='signin'  onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}>
            <div className="signInBlock">
              <div className="inputGroup" style={{ position: 'relative' }}>
                <input
                  type="email"
                  id="emailInput"
                  tabIndex={1}
                  value={email}
                  ref={emailRef}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter Email"
                  style={{ paddingRight: '2.5em' }}
                  autoComplete="username"
                />
              </div>

              <div className="inputGroup" style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="passwordInput"
                  tabIndex={2}
                  value={password}
                  onChange={(e) => {
                    //const newValue = e.target.value;
                    //console.log("🔑 Password input changed (Zustand):", e.target.value);
                    setPassword(e.target.value)
                  }}
                  placeholder="Enter Password"
                  style={{ paddingRight: '2.5em' }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  aria-label="Toggle password visibility"
                  tabIndex={3}
                  style={{
                    position: 'absolute',
                    right: '0.5em',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.2em',
                    padding: 0
                  }}
                >
                  {showPassword ? (
                    <img src="/assets/app/eye-open-512.png" alt="Hide password" style={{ width: '1em', height: '1em' }} />
                  ) : (
                    <img src="/assets/app/eye-close-512.png" alt="Show password" style={{ width: '1em', height: '1em' }} />
                  )}
                </button>
              </div>

      

          {/* End SignIn Block */}

          <p className="infoText">Sign in or sign up to create a new account</p>
          <p className="loading" style={{ color: "red", fontSize: ".5em" }}></p>

          <div className="buttonContainer">
              <button
                className="signInButton"
                id="signInButton"
                tabIndex={4}
                onClick={handleSubmit}
                style={org?.buttonStyle}
              >
                Sign In
              </button>
              <button
                className="signUpButton"
                tabIndex={5}
                onClick={() => {
                  const queryString = window.location.search; // grab entire query string, e.g. ?id=foo
                  const signupUrl = queryString ? `/signup${queryString}` : "/signup";
                  window.location.href = signupUrl;
                }}
                style={org?.buttonStyle}
              >
                Sign Up
              </button>

            </div>
            <p className="forgotPassword" style={{ marginTop: 12, textAlign: 'center' }}>
              <a
                id="forgotPasswordLink"
                href={botId ? `/reset-password?id=${encodeURIComponent(botId)}` : '/reset-password'}
                style={{ color: 'inherit', fontSize: '0.9rem' }}
              >
                Forgot password?
              </a>
            </p>
            </div>
          </form>
        <div id="spacer" style={{ height: "100px" }}></div>
        </div>
      </div>
 

        <div
          id="signInFooter"
          className="signInFooter"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            padding: "1em 1em",
            width: "100%",
            fontSize:".7em",
            marginTop: "auto", 
          }}
        >
          <div style={{ textAlign: "center" }}>
            {org ? (
              <div className="footerDiv" id="signInDisclaimer-links">
                {org.footer.disclaimer}
                <br />
                <span>{org.footer.copyright}</span>
                {org.footer.links.map((link, index) => (
                  <React.Fragment key={index}>
                    &nbsp;|&nbsp;
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      <span >{link.label}</span>
                    </a>
                  </React.Fragment>
                ))}
              </div>
            ) : (
                
                <div className="footerDiv" id="signInDisclaimer-text">
             
                <br/>
                <a href="https://www.cloudmediaworks.com/terms-of-use" target="_blank">
                  Terms of Use &nbsp;|&nbsp;
                </a>
                &nbsp;|&nbsp;
                <a href="https://www.cloudmediaworks.com/privacy" target="_blank">
                  <span >Privacy Statement</span>&nbsp;|&nbsp;
                </a>
                &nbsp;|&nbsp;
                <a href="https://www.cloudmediaworks.com/cookies" target="_blank">
                  <span >Cookies Settings</span>&nbsp;|&nbsp;
                </a>&nbsp;&nbsp;&nbsp;
                &nbsp;|&nbsp;
   
              </div>
              
            )}
          <div>
            <a href="/admin" style={{ width: '29px', height: '29px', position: 'absolute', left: '94vw', bottom: '10px'}}><span></span>
            </a>
          </div>

            <br/>
          </div>
        </div>
      </div>
    </div>
  );
};

const PMPT_SESSION_ID = 'pmpt_68d6adf9c1148195bf696618460b883301e26765bc41a937'; // Open AI Audio (dead, Phase 6)

/**
 * [S2-B B2] /signin resolves its bot from the DB BEFORE rendering: ?id= (or a stored id) via
 * GET /agent-bootstrap?id=, else the HOST via ?host= (the DNS routing that used to be dead code
 * behind the Nova default). Hard fail (decision A): unknown ⇒ not-found, botapi down ⇒ unavailable,
 * nothing selected and no host match ⇒ "choose an assistant". Never Nova.
 * The guest decision is the record's `guestmode` (was system.json); the two literal-id prompt
 * cases are pre-existing and unchanged.
 */
const SignInPage = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const requestedId = queryParams.get('id') || getStoredBotId() || '';
  const pre = usePreAuthOrg(requestedId === PMPT_SESSION_ID ? null : requestedId);
  const prompt = queryParams.get('prompt') || '';
  if (requestedId === PMPT_SESSION_ID) return <SignInPageGuest />;
  if (pre.status === 'loading') return <div data-component="SignInPage-loading" />;
  if (pre.status !== 'ready') {
    return <PreAuthNotice status={!requestedId && pre.status === 'not-found' ? 'choose' : pre.status} />;
  }
  const isGuest =
    pre.record?.guestmode === true ||
    ((pre.botId === 'asst_SRqG7nOQ8boguG6EeEPkpNlE' || pre.botId === 'asst_k6gkeaM6VZsbvOjfBNt6BFrK') && !!prompt);
  if (isGuest) {
    console.log(`🔍 Guest SignIn detected (record guestmode) for ${pre.botId}`);
    return <SignInPageGuest />;
  }
  return <SignInPageAuthed botId={pre.botId} org={pre.org} />;
};

export default SignInPage;
